"use client";

import * as React from "react";
import { Gift } from "lucide-react";
import { PopoverContent } from "@/components/ui/popover";
import {
  articlePopoverCompactSheetClass,
  useHeaderPopoverLayout,
  type ArticlePopoverPlacement,
} from "./popover-placement";
import { CORE_COLORS, CORE_FONT } from "@/lib/theme/core";
import { CopyLinkRow, PopoverHeader, SocialIconsGrid } from "./share-popover";
import { toast } from "sonner";

export function GiftPopoverContent({
  onClose,
  placement = "rail",
}: {
  onClose: () => void;
  placement?: ArticlePopoverPlacement;
}) {
  // Gift state: false = not yet generated; true = sharable link active.
  const [gifted, setGifted] = React.useState(false);

  const monthlyQuotaTotal = 5;
  const remaining = gifted ? monthlyQuotaTotal - 1 : monthlyQuotaTotal;

  const layout = useHeaderPopoverLayout(placement, {
    desktopWidth: 320,
    headerAlign: "start",
  });
  const padX = layout.isCompactHeader ? 16 : 24;

  return (
    <PopoverContent
      side={layout.side}
      align={layout.align}
      sideOffset={12}
      collisionPadding={layout.collisionPadding}
      className={`bg-white p-0${layout.isCompactHeader ? ` ${articlePopoverCompactSheetClass()}` : ""}`}
      style={{
        zIndex: 60,
        width: layout.width,
        borderRadius: "8px",
        border: `1px solid rgba(0,0,0,0.15)`,
        fontFamily: CORE_FONT.sans,
      }}
    >
      <div className="flex flex-col">
        <PopoverHeader title="贈送文章" onClose={onClose} padX={padX} />

        <div className="flex flex-col gap-[10px] px-[24px] pb-[12px] text-center">
          <p
            style={{
              fontFamily: CORE_FONT.sans,
              fontSize: "13px",
              color: CORE_COLORS.textSecondary,
              lineHeight: 1.6,
            }}
          >
            贈送此文章後，收件人毋須訂閱即可閱讀全文。
          </p>
          <p
            style={{
              fontFamily: CORE_FONT.sans,
              fontSize: "12px",
              fontWeight: 700,
              color: CORE_COLORS.textPrimary,
              lineHeight: "16px",
            }}
          >
            訂戶每月 5 篇免費贈閱。
            <span style={{ color: CORE_COLORS.brandGreenDeep, marginLeft: 4 }}>
              本月剩餘：{remaining} / {monthlyQuotaTotal}
            </span>
          </p>
        </div>

        {gifted ? (
          <>
            <div
              className="mx-[24px] mb-[12px] rounded-[3px]"
              style={{
                padding: "10px 12px",
                backgroundColor: "rgba(37,156,115,0.08)",
                border: `1px solid rgba(37,156,115,0.3)`,
              }}
            >
              <p
                style={{
                  fontFamily: CORE_FONT.sans,
                  fontSize: "12px",
                  color: CORE_COLORS.textPrimary,
                  lineHeight: 1.55,
                }}
              >
                此文章已成為贈閱文章。重複分享同一篇文章不會額外計算進贈閱配額。
              </p>
            </div>
            <SocialIconsGrid padX={padX} />
            <CopyLinkRow
              url="https://www.hkej.com/share/gift/abc123def456"
              padX={padX}
            />
          </>
        ) : (
          <div className="px-[24px] pb-[20px] pt-[8px]">
            <button
              type="button"
              onClick={() => {
                setGifted(true);
                // QA: the action produced a link but said nothing.
                toast.success("已產生贈閱連結");
              }}
              className="inline-flex w-full items-center justify-center gap-[8px] transition-colors hover:bg-[color:var(--green-dark)]"
              style={
                {
                  fontFamily: CORE_FONT.sans,
                  fontSize: "14px",
                  fontWeight: 700,
                  color: "white",
                  lineHeight: "20px",
                  padding: "12px 20px",
                  backgroundColor: CORE_COLORS.brandGreen,
                  borderRadius: "3px",
                  ["--green-dark" as string]: CORE_COLORS.brandGreenDark,
                } as React.CSSProperties
              }
            >
              <Gift className="size-[16px]" strokeWidth={2.2} />
              產生贈閱連結
            </button>
          </div>
        )}
      </div>
    </PopoverContent>
  );
}
